<template>
  <v-container fluid>
    <v-row>
      <v-col>
        <loading-spinner v-if="isLoading"></loading-spinner>
        <!-- 페이징 테이블 -->
        <paginated-table
          v-if="!isLoading"
          :data="reviewers"
          :columns="tableColumns"
          :title="tableTitle"
          @updatePagination="updatePagination"
        >
          <!-- 추가 버튼 -->
          <template v-slot:top-buttons>
            <custom-button
              @click="openModal"
              color="primary"
              :label="buttons.add"
              rounded
              gradient
            ></custom-button>
          </template>
        </paginated-table>

        <!-- 심사위원 추가 -->
        <reviewer-select-form
          v-if="showAddModal && isMemberLoaded"
          :columns="modalColumns"
          v-model="members"
          @close="controlAddModal"
          @send-data="handleAddReviewer"
        ></reviewer-select-form>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import PaginatedTable from "@/components/Tables/PaginatedTable.vue";
import ReviewerSelectForm from "@/components/specific/Reviewer/ReviewerSelectForm.vue";
import CustomButton from "@/components/common/buttons/CustomButton.vue";
import LoadingSpinner from "@/components/common/Spinners/LoadingSpinner.vue";
import {
  getManagementColumns,
  getSelectMemberColumns,
} from "@/views/ReviewerView/columns";
import { deletePopup, success } from "@/views/ReviewerView/popup";
import { managementLabel } from "@/views/ReviewerView/label";

import { reviewerApi, memberApi } from "@/api/api";

export default {
  /**
   * 심사위원 관리 페이지 view
   */
  name: "reviewers-management",
  components: {
    PaginatedTable,
    ReviewerSelectForm,
    CustomButton,
    LoadingSpinner,
  },
  data() {
    return {
      // label
      tableTitle: managementLabel.tableTitle,
      tableColumns: getManagementColumns({
        delete: this.handleDelete,
      }),

      // fields & columns
      modalColumns: getSelectMemberColumns({}),
      buttons: managementLabel.buttons,

      // boolean
      showAddModal: false,
      isLoading: false,
      isMemberLoaded: false,

      // init data
      reviewers: {
        page_info: {
          page: 1,
          size: 5,
          total_elements: 5,
          total_pages: 1,
        },
        elements: [],
      },
      members: [],
    };
  },
  methods: {
    /**
     * 모달 컨트롤
     */
    async openModal() {
      await this.fetchMembers();
      await this.controlAddModal();
    },

    async controlAddModal() {
      this.showAddModal = !this.showAddModal;
    },

    /**
     * Pagination 타입 명세
     * @typedef {Object} Conditions
     * @property {string} page - 페이지
     * @property {string} size - 사이즈
     * @property {string} total_elements - 전체 데이터 수
     * @property {string} total_pages - 전체 페이지 수
     */

    /**
     * 페이지네이션 업데이트 메서드
     *
     * @description: 페이징 정보가 변경되면 하위 컴포넌트에서 정보를 받아 업데이트 및 API 요청
     * @params {Pagination} pagination
     */
    updatePagination(pagination) {
      this.reviewers.page_info.size = pagination.size;
      this.reviewers.page_info.page = pagination.page;

      this.fetchReviewers(
        this.reviewers.page_info.size,
        this.reviewers.page_info.page
      );
    },

    /**
     * *****************************************************************************************
     * 버튼 액션
     * *****************************************************************************************
     */

    /**
     * 추가 액션
     */
    handleAddReviewer(data) {
      const reviewer = {
        started_at: data.date.startedAt,
        ended_at: data.date.endedAt,
        name: data.selectedItems[0].name,
        member_idx: data.selectedItems[0].idx,
        email: data.selectedItems[0].email,
      };

      this.createReviewer(reviewer);
      this.controlAddModal();
    },

    async handleDelete(reviewer) {
      const isConfirmed = await deletePopup(reviewer);

      if (isConfirmed) {
        const isDeleted = await this.deleteReviewer(reviewer);

        if (isDeleted) {
          await success();
        }
      }
    },

    /**
     * *****************************************************************************************
     * API 요청
     * *****************************************************************************************
     */

    /**
     * 리뷰어 목록 조회 API 요청 메서드
     */
    fetchReviewers(size, page) {
      const params = {
        size: size,
        page: page,
        sort_by: "idx",
        direction: "desc",
      };

      this.isLoading = true;
      reviewerApi
        .getReviewers(params)
        .then((response) => {
          if (response && response.status === 200) {
            this.reviewers = response.data.response;
          } else {
            alert(managementLabel.apiRequestFailMessage);
          }
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    /**
     * 리뷰어가 아닌 회원 목록 조회 API 요청 메서드
     */
    async fetchMembers() {
      this.isMemberLoaded = false;

      try {
        const response = await memberApi.getNonReviewerMembers();

        if (response && response.status === 200) {
          this.members = response.data.response;
        } else {
          alert(managementLabel.apiRequestFailMessage);
        }
        this.isMemberLoaded = true;
      } catch {
        this.isMemberLoaded = true;
      }
    },

    /**
     * @ 삭제 API 요청
     * @param reviewer
     */
    async deleteReviewer(reviewer) {
      try {
        const response = await reviewerApi.deleteReviewer(reviewer);

        if (response && response.status === 204) {
          await this.fetchReviewers(
            this.reviewers.page_info.size,
            this.reviewers.page_info.page
          );
          return true;
        } else {
          alert(managementLabel.apiRequestFailMessage);
          return false;
        }
      } catch (error) {
        alert(managementLabel.apiRequestFailMessage);
        return false;
      }
    },

    /**
     * @ 생성 API 요청
     * @param reviewer
     */
    async createReviewer(reviewer) {
      try {
        const response = await reviewerApi.createReviewer(reviewer);

        if (response && response.status === 201) {
          await this.fetchReviewers(
            this.reviewers.page_info.size,
            this.reviewers.page_info.page
          );
          return true;
        } else {
          alert(managementLabel.apiRequestFailMessage);
          return false;
        }
      } catch (error) {
        alert(managementLabel.apiRequestFailMessage);
        return false;
      }
    },
  },
  mounted() {
    this.fetchReviewers(
      this.reviewers.page_info.size,
      this.reviewers.page_info.page
    );
  },
};
</script>

<style lang="sass" scoped></style>
